"use client";

import { UnstyledList } from "../styled-common/unstyledList";
import { BodyText, Checkbox } from "@gnist/design-system";
import { css, styled } from "styled-components";
import { breakpointDesktop } from "@gnist/design-system";
import { CheckboxFilter, DepartmentsFilter } from "./types/filters.types";
import { Collapse } from "@/components/collapse";
import { isMobile } from "@/utils/isMobile";
import { translateFilter } from "@/utils/translateFilter";
import { tokens } from "@gnist/themes/tokens.css.js";

const Heading = styled(BodyText)`
  margin: 0 0 ${tokens.spacing.xxs} 0;
  line-height: 150%;
  display: none;
  font-weight: ${tokens.typeface.weight.semibold};

  @media screen and (min-width: ${breakpointDesktop}) {
    display: inherit;
  }
`;

const Wrapper = styled.li`
  padding-top: ${tokens.spacing.s};
  padding-left: ${tokens.size.xxs};

  @media screen and (min-width: ${breakpointDesktop}) {
    padding-top: ${tokens.spacing.base};
  }

  ${(props) =>
    props.className === "filterGroup" &&
    css`
      padding-bottom: ${tokens.spacing.s};
      border-bottom: solid 1px;
      border-bottom-color: ${tokens.color.outline};

      @media screen and (min-width: ${breakpointDesktop}) {
        padding-left: unset;
        padding-bottom: unset;
        border-bottom: unset;
      }
    `}

  ${(props) =>
    props.className === "models" &&
    css`
      @media screen and (min-width: ${breakpointDesktop}) {
        padding-left: ${tokens.spacing.xs};
      }
    `}
`;

interface Props {
  headingText: string;
  checkboxFilters: CheckboxFilter[];
  toggleFilter: (toggleFilterKey: string, toggleModelKey?: string) => void;
}

const groupDepartmentFilters = (filters: DepartmentsFilter[]): Map<string, DepartmentsFilter[]> => {
  const grouped = new Map<string, DepartmentsFilter[]>();
  for (const filter of filters) {
    const groupKey = filter.group ?? "";
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, []);
    }
    grouped.get(groupKey)!.push(filter);
  }
  return grouped;
};

const FilterList = ({
  checkboxFilters,
  toggleFilter,
}: Omit<Props, "headingText">) => {
  // Check if we have grouped DepartmentsFilter
  const hasGroupedDepartments =
    checkboxFilters.length > 0 &&
    checkboxFilters[0].kind === "Departments" &&
    (checkboxFilters[0] as DepartmentsFilter).group !== undefined;

  if (hasGroupedDepartments) {
    const groupedFilters = groupDepartmentFilters(
      checkboxFilters as DepartmentsFilter[],
    );

    return (
      <UnstyledList>
        {Array.from(groupedFilters.entries()).map(([groupName, departments]) => {
          const isGroupExpanded = departments[0]?.isGroupExpanded ?? false;

          return (
            <Wrapper className={"filterGroup"} key={groupName}>
              <Checkbox
                label={groupName}
                name={groupName}
                value={isGroupExpanded}
                onChange={() => toggleFilter(groupName)}
              />
              {isGroupExpanded && departments.length > 0 && (
                <UnstyledList>
                  {departments.map((department) => (
                    <Wrapper key={department.key} className="models">
                      <Checkbox
                        label={translateFilter(department) ?? department.name}
                        name={department.name}
                        value={department.isActive}
                        onChange={() => toggleFilter(groupName, department.key)}
                      />
                    </Wrapper>
                  ))}
                </UnstyledList>
              )}
            </Wrapper>
          );
        })}
      </UnstyledList>
    );
  }

  return (
    <UnstyledList>
      {checkboxFilters.map((filter) => (
        <Wrapper className={"filterGroup"} key={filter.name}>
          <Checkbox
            label={translateFilter(filter) ?? filter.name}
            name={filter.name}
            value={filter.isActive}
            onChange={() => toggleFilter(filter.key)}
          />
          {/*  If the filter is a brands/models filter, render a sublist of the models*/}

          {filter.kind === "BrandWithModels" &&
            filter.isActive &&
            filter.models.length > 0 && (
              <UnstyledList>
                {filter.models.map((model) => (
                  <Wrapper key={model.name} className="models">
                    <Checkbox
                      label={model.name}
                      name={model.name}
                      value={model.isActive}
                      onChange={() => toggleFilter(filter.key, model.key)}
                    />
                  </Wrapper>
                ))}
              </UnstyledList>
            )}
        </Wrapper>
      ))}
    </UnstyledList>
  );
};

export const DefaultCheckboxGroup = ({
  headingText,
  checkboxFilters,
  toggleFilter,
}: Props) => {
  return (
    <div>
      {headingText && <Heading>{headingText}</Heading>}
      {isMobile() ? (
        <FilterList
          checkboxFilters={checkboxFilters}
          toggleFilter={toggleFilter}
        />
      ) : (
        <Collapse
          id={`filter-${headingText}`}
          backgroundColorToken={tokens.color.background}
          collapsedHeight="200"
          buttonLabel={{ collapse: "Vis mindre", expand: "Vis mer" }}
          showCollapsedOverlay={false}
          shouldOverrideContentHeight
          shouldOpenOnContentClick
          shouldOpenByDefault={checkboxFilters.some((f) => f.isActive)}
        >
          <FilterList
            checkboxFilters={checkboxFilters}
            toggleFilter={toggleFilter}
          />
        </Collapse>
      )}
    </div>
  );
};

export default DefaultCheckboxGroup;
